import React from 'react';
import { useReport } from '../hooks/useReport';
import InFlightPanel from './InFlightPanel';
import SectionToggle from './SectionToggle';
import TurnaroundChart from './TurnaroundChart';
import { formatHours } from '../../../utils/duration';

const CARD = 'rounded-md border border-dash-border bg-dash-card p-4';

// Tier-2 metrics (Rejection Reasons, Rework Rate, Time Pending per Level) used
// to render here as locked "requires backend support" placeholder cards —
// hidden per request until the backend can actually feed them (see git history
// for the card markup).

const OperationalSection = ({ filters }) => {
  // The turnaround chart and table are fed from THIS same report-scoped array —
  // no extra fetch, and never /dashboard/turnaround-by-stage (wrong population).
  const { data: pipeline, loading } = useReport('/reports/mis/pipeline', filters);
  const turnaround = pipeline?.turnaround || [];
  const byLevel = pipeline?.byLevel || {};
  const levels = ['LEVEL_1', 'LEVEL_2', 'FINAL'];
  const [turnView, setTurnView] = React.useState('chart');

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold text-dash-foreground">Operational / Pipeline</h2>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* Turnaround by stage — table (default) or local bottleneck chart */}
        <div className={CARD}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-dash-foreground">Turnaround by Stage</h3>
            <div className="flex items-center gap-2">
              {pipeline ? (
                <span className="text-[11px] text-dash-muted-foreground">
                  {pipeline.feedingCounts.live} live · {pipeline.feedingCounts.deleted} deleted
                </span>
              ) : null}
              <SectionToggle value={turnView} onChange={setTurnView} />
            </div>
          </div>
          {turnView === 'chart' ? (
            <TurnaroundChart rows={turnaround} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="border-b border-dash-border text-dash-muted-foreground">
                    <th className="py-1 pr-2 text-left font-medium">Stage</th>
                    <th className="py-1 px-2 text-right font-medium">Avg</th>
                    <th className="py-1 px-2 text-right font-medium">Median</th>
                    <th className="py-1 px-2 text-right font-medium">Count</th>
                    <th className="py-1 pl-2 text-right font-medium">% cycle</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && turnaround.length === 0 ? (
                    <tr><td colSpan={5} className="py-3 text-center text-dash-muted-foreground">Loading…</td></tr>
                  ) : turnaround.length === 0 ? (
                    <tr><td colSpan={5} className="py-3 text-center text-dash-muted-foreground">No data available</td></tr>
                  ) : (
                    turnaround.map((r) => (
                      <tr key={r.stage} className="border-b border-dash-border/50">
                        <td className="py-1 pr-2 text-dash-foreground">{r.stage}</td>
                        <td className="py-1 px-2 text-right tabular-nums text-dash-foreground">{formatHours(r.avg_hours)}</td>
                        <td className="py-1 px-2 text-right tabular-nums text-dash-muted-foreground">{formatHours(r.median_hours)}</td>
                        <td className="py-1 px-2 text-right tabular-nums text-dash-muted-foreground">{r.count}</td>
                        <td className="py-1 pl-2 text-right tabular-nums text-dash-foreground">{r.pct_of_cycle}%</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Approvals by level & status (scoped) */}
        <div className={CARD}>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-dash-foreground">Approvals by Level</h3>
            <span className="text-[11px] text-dash-muted-foreground">{pipeline?.rejections ?? 0} rejections</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-dash-border text-dash-muted-foreground">
                  <th className="py-1 pr-2 text-left font-medium">Level</th>
                  <th className="py-1 px-2 text-right font-medium">Approved</th>
                  <th className="py-1 px-2 text-right font-medium">Rejected</th>
                  <th className="py-1 pl-2 text-right font-medium">Pending</th>
                </tr>
              </thead>
              <tbody>
                {levels.map((l) => (
                  <tr key={l} className="border-b border-dash-border/50">
                    <td className="py-1 pr-2 text-dash-foreground">{l.replace('_', ' ')}</td>
                    <td className="py-1 px-2 text-right tabular-nums text-dash-foreground">{byLevel[l]?.APPROVED ?? 0}</td>
                    <td className="py-1 px-2 text-right tabular-nums text-dash-destructive">{byLevel[l]?.REJECTED ?? 0}</td>
                    <td className="py-1 pl-2 text-right tabular-nums text-dash-muted-foreground">{byLevel[l]?.PENDING ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Out-of-scope current in-flight snapshot */}
      <InFlightPanel />
    </section>
  );
};

export default OperationalSection;
